import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Drawer } from "@/components/ui/Drawer";
import { Icon } from "@/components/ui/Icon";
import { MetricCard } from "@/components/ui/MetricCard";
import { ProjectionLag } from "@/components/ui/ProjectionLag";
import { ErrorState } from "@/components/ui/States";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { SegmentedFilter, Toolbar } from "@/components/ui/Toolbar";
import { request } from "@/lib/api/client";
import { useList } from "@/lib/api/queries";
import { useProjectionStatus, useReport } from "@/lib/api/reports";
import { dateTime, duration, qty, relative } from "@/lib/format";
import { roleLabel, statusLabel } from "@/lib/labels";
import "./staff.css";

/** `GET /reports/kpi/employees` — tur bo'yicha (yoki jami) yig'indi. */
interface StaffTotals {
  task_type?: string;
  task_count: number;
  line_count: number;
  actual_qty: string;
  work_seconds: number;
  wait_seconds: number;
  shortage_count: number;
  excess_count: number;
  penalty_count: number;
}

interface StaffItem extends StaffTotals {
  employee_id: string;
  active_days: number;
  last_completed_at?: string;
  open_tasks: number;
  by_type: StaffTotals[];
}

interface StaffResponse {
  from: string;
  to: string;
  items: StaffItem[];
}

interface User {
  id: string;
  username: string;
  full_name: string;
  is_active: boolean;
  roles: string[];
}

/** Jadval qatori: katalogdagi xodim + davr natijasi (yo'q bo'lsa nol). */
interface Row {
  id: string;
  name: string;
  roles: string[];
  isActive: boolean;
  stats: StaffItem;
}

type Preset = "today" | "week" | "month" | "custom";

const EMPTY: Omit<StaffItem, "employee_id"> = {
  task_count: 0,
  line_count: 0,
  actual_qty: "0",
  work_seconds: 0,
  wait_seconds: 0,
  shortage_count: 0,
  excess_count: 0,
  penalty_count: 0,
  active_days: 0,
  open_tasks: 0,
  by_type: [],
};

/** Server kunni UTC sanasi bilan hisoblaydi (KPI `bucket_date`). */
function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function presetRange(preset: Exclude<Preset, "custom">): { from: string; to: string } {
  const today = new Date();
  const back = (days: number) => isoDay(new Date(today.getTime() - days * 86_400_000));
  switch (preset) {
    case "today":
      return { from: isoDay(today), to: isoDay(today) };
    case "week":
      return { from: back(6), to: isoDay(today) };
    case "month":
      return { from: back(29), to: isoDay(today) };
  }
}

/** Soatiga satr: sof ish vaqti bo'yicha. Ish vaqti nol — o'lchab bo'lmaydi. */
function perHour(lines: number, seconds: number): number | null {
  if (seconds <= 0) return null;
  return lines / (seconds / 3600);
}

function perHourText(lines: number, seconds: number): string {
  const value = perHour(lines, seconds);
  return value === null ? "—" : value.toFixed(1);
}

/**
 * KPI va hisobotlar — BARCHA xodimlar ro'yxati va davr statistikasi.
 *
 * Ro'yxat identity katalogidan olinadi, natija reporting-service dan
 * (`/reports/kpi/employees`). Ilgari sahifa `kpi` hisobotining kun × tur
 * satrlarini 200 tagacha ko'rsatardi: davrda ishlamagan xodim umuman
 * ko'rinmas, ko'p xodimli omborda esa ro'yxat birinchi kunlardayoq
 * kesilib qolardi. Natijasi nol xodim ham ko'rsatiladi — davrda
 * ishlamaganlik ham ko'rsatkich.
 */
export function StaffKpi({ hint }: { hint: string }) {
  const [preset, setPreset] = useState<Preset>("week");
  const [custom, setCustom] = useState(() => presetRange("week"));
  const [search, setSearch] = useState("");
  const [withInactive, setWithInactive] = useState(false);
  const [selected, setSelected] = useState<Row | null>(null);

  const range = preset === "custom" ? custom : presetRange(preset);
  const status = useProjectionStatus();

  const staff = useQuery({
    queryKey: ["reporting", "/api/v1/reports/kpi/employees", range],
    queryFn: ({ signal }) =>
      request<StaffResponse>("reporting", "/api/v1/reports/kpi/employees", {
        query: { from: range.from, to: range.to },
        signal,
      }),
  });
  const users = useList<User>("identity", "/api/v1/users", { limit: 500 });

  const rows = useMemo<Row[]>(() => {
    const stats = new Map((staff.data?.items ?? []).map((item) => [item.employee_id, item] as const));
    const result: Row[] = [];
    const seen = new Set<string>();
    for (const user of users.data?.items ?? []) {
      seen.add(user.id);
      const item = stats.get(user.id);
      // Nofaol xodim faqat davrda natijasi bo'lsa yoki so'ralganda ko'rinadi.
      if (!user.is_active && !item && !withInactive) continue;
      result.push({
        id: user.id,
        name: user.full_name || user.username,
        roles: user.roles ?? [],
        isActive: user.is_active,
        stats: item ?? { ...EMPTY, employee_id: user.id },
      });
    }
    // Katalogda yo'q ijrochi (masalan tizim yoki o'chirilgan hisob).
    for (const item of staff.data?.items ?? []) {
      if (seen.has(item.employee_id)) continue;
      result.push({ id: item.employee_id, name: "Noma'lum xodim", roles: [], isActive: false, stats: item });
    }
    return result.sort((a, b) => b.stats.task_count - a.stats.task_count || a.name.localeCompare(b.name));
  }, [staff.data, users.data, withInactive]);

  const needle = search.trim().toLowerCase();
  const visible = rows.filter((row) =>
    needle ? [row.name, ...row.roles.map(roleLabel)].some((value) => value.toLowerCase().includes(needle)) : true,
  );

  const totals = visible.reduce(
    (sum, row) => ({
      tasks: sum.tasks + row.stats.task_count,
      lines: sum.lines + row.stats.line_count,
      seconds: sum.seconds + row.stats.work_seconds,
      active: sum.active + (row.stats.task_count > 0 ? 1 : 0),
      issues: sum.issues + row.stats.shortage_count + row.stats.excess_count,
    }),
    { tasks: 0, lines: 0, seconds: 0, active: 0, issues: 0 },
  );

  const columns: Array<Column<Row>> = [
    {
      key: "name",
      header: "Xodim",
      value: (row) => row.name,
      render: (row) => (
        <div className="staff-name">
          <span>
            {row.name}
            {!row.isActive && <span className="staff-muted"> · nofaol</span>}
          </span>
          {row.roles.length > 0 && <span className="staff-muted">{row.roles.map(roleLabel).join(", ")}</span>}
        </div>
      ),
    },
    { key: "tasks", header: "Topshiriq", align: "right", value: (row) => row.stats.task_count, render: (row) => <span className="num">{row.stats.task_count}</span> },
    { key: "lines", header: "Satr", align: "right", value: (row) => row.stats.line_count, render: (row) => <span className="num">{row.stats.line_count}</span> },
    { key: "qty", header: "Dona", align: "right", value: (row) => Number(row.stats.actual_qty), render: (row) => <span className="num">{qty(row.stats.actual_qty)}</span> },
    { key: "work", header: "Ish vaqti", align: "right", value: (row) => row.stats.work_seconds, render: (row) => <span className="num">{row.stats.work_seconds > 0 ? duration(row.stats.work_seconds) : "—"}</span> },
    {
      key: "rate",
      header: "Satr/soat",
      align: "right",
      value: (row) => perHour(row.stats.line_count, row.stats.work_seconds) ?? -1,
      render: (row) => <span className="num">{perHourText(row.stats.line_count, row.stats.work_seconds)}</span>,
    },
    {
      key: "shortage",
      header: "Kamomad",
      align: "right",
      value: (row) => row.stats.shortage_count,
      render: (row) => <span className="num" style={{ color: row.stats.shortage_count > 0 ? "var(--error)" : undefined }}>{row.stats.shortage_count}</span>,
    },
    {
      key: "excess",
      header: "Ortiqcha",
      align: "right",
      value: (row) => row.stats.excess_count,
      render: (row) => <span className="num" style={{ color: row.stats.excess_count > 0 ? "var(--warning)" : undefined }}>{row.stats.excess_count}</span>,
    },
    { key: "penalty", header: "Jarima", align: "right", value: (row) => row.stats.penalty_count, render: (row) => <span className="num">{row.stats.penalty_count}</span> },
    { key: "days", header: "Faol kun", align: "right", value: (row) => row.stats.active_days, render: (row) => <span className="num">{row.stats.active_days}</span> },
    { key: "open", header: "Qo'lida", align: "right", value: (row) => row.stats.open_tasks, render: (row) => <span className="num">{row.stats.open_tasks}</span> },
    {
      key: "last",
      header: "Oxirgi ish",
      value: (row) => row.stats.last_completed_at ?? "",
      render: (row) =>
        row.stats.last_completed_at ? (
          <span title={dateTime(row.stats.last_completed_at)}>{relative(row.stats.last_completed_at)}</span>
        ) : (
          <span className="staff-muted">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="KPI va hisobotlar"
        hint={hint}
        actions={
          <>
            <button type="button" className="btn btn-outlined" onClick={() => exportCsv(visible, range)} disabled={visible.length === 0}>
              <Icon name="download" size={16} />
              CSV
            </button>
            <ProjectionLag status={status.data?.items} />
          </>
        }
      />

      {staff.error && <ErrorState error={staff.error} onRetry={() => void staff.refetch()} />}

      <section className="metrics">
        <MetricCard label="Xodimlar" value={visible.length} hint={`${totals.active} tasi davrda ishlagan`} icon="shield" loading={users.isLoading} />
        <MetricCard label="Topshiriq" value={totals.tasks} hint="yakunlangan" icon="task" loading={staff.isLoading} />
        <MetricCard label="Satr" value={totals.lines} hint={`soatiga ${perHourText(totals.lines, totals.seconds)}`} icon="layers" loading={staff.isLoading} />
        <MetricCard label="Tafovut" value={totals.issues} hint="kamomad + ortiqcha" icon="discrepancy" accent={totals.issues > 0 ? "warning" : undefined} loading={staff.isLoading} />
      </section>

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Ism yoki rol bo'yicha…"
        onRefresh={() => {
          void staff.refetch();
          void users.refetch();
        }}
        refreshing={staff.isFetching || users.isFetching}
      >
        <SegmentedFilter
          value={preset}
          onChange={(value) => {
            if (value === "custom" && preset !== "custom") setCustom(range);
            setPreset(value);
          }}
          options={[
            { value: "today", label: "Bugun" },
            { value: "week", label: "7 kun" },
            { value: "month", label: "30 kun" },
            { value: "custom", label: "Davr" },
          ]}
        />
        {preset === "custom" && (
          <>
            <label className="staff-date">
              <span>Dan</span>
              <input className="input" type="date" value={custom.from} max={custom.to}
                onChange={(event) => event.target.value && setCustom({ ...custom, from: event.target.value })} />
            </label>
            <label className="staff-date">
              <span>Gacha</span>
              <input className="input" type="date" value={custom.to} min={custom.from}
                onChange={(event) => event.target.value && setCustom({ ...custom, to: event.target.value })} />
            </label>
          </>
        )}
        <label className="staff-toggle">
          <input type="checkbox" checked={withInactive} onChange={(event) => setWithInactive(event.target.checked)} />
          Nofaollar ham
        </label>
      </Toolbar>

      <DataTable
        tableId="staff-kpi"
        columns={columns}
        rows={visible}
        rowKey={(row) => row.id}
        isLoading={staff.isLoading || users.isLoading}
        error={users.error}
        onRetry={() => void users.refetch()}
        onRowClick={setSelected}
        activeKey={selected?.id}
        emptyTitle={needle ? "Qidiruv natijasi bo'sh" : "Xodim topilmadi"}
        emptyHint="Xodimlar «Foydalanuvchi va rollar» bo'limida qo'shiladi."
        footer={
          <span>
            {visible.length} xodim · {range.from === range.to ? range.from : `${range.from} — ${range.to}`} (UTC kunlari)
          </span>
        }
      />

      {selected && <StaffDetail row={selected} range={range} onClose={() => setSelected(null)} />}
    </>
  );
}

/** Bitta xodim: tur bo'yicha taqsimot, kunlar va oxirgi topshiriqlar. */
function StaffDetail({ row, range, onClose }: { row: Row; range: { from: string; to: string }; onClose: () => void }) {
  const daily = useReport("kpi", { assignee_id: row.id, from: `${range.from}T00:00:00Z`, to: `${range.to}T23:59:59Z`, limit: 500 });
  const tasks = useReport("tasks", { assignee_id: row.id, limit: 30 });

  const days = useMemo(() => {
    const map = new Map<string, { tasks: number; lines: number; qty: number; seconds: number; issues: number }>();
    for (const item of daily.data?.items ?? []) {
      const day = (item.bucket_date ?? "").slice(0, 10);
      const entry = map.get(day) ?? { tasks: 0, lines: 0, qty: 0, seconds: 0, issues: 0 };
      entry.tasks += Number(item.task_count ?? 0);
      entry.lines += Number(item.line_count ?? 0);
      entry.qty += Number(item.actual_qty ?? 0);
      entry.seconds += Number(item.work_seconds ?? 0);
      entry.issues += Number(item.shortage_count ?? 0) + Number(item.excess_count ?? 0);
      map.set(day, entry);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [daily.data]);

  const stats = row.stats;
  return (
    <Drawer label="Xodim statistikasi">
      <div className="drawer-head">
        <div>
          <div className="dialog-title">{row.name}</div>
          <div className="dialog-desc">
            {row.roles.map(roleLabel).join(", ") || "Rol berilmagan"} · {range.from === range.to ? range.from : `${range.from} — ${range.to}`}
          </div>
        </div>
        <button type="button" className="btn btn-icon" onClick={onClose} aria-label="Yopish">
          <Icon name="close" />
        </button>
      </div>

      <div className="drawer-body">
        <dl className="kv">
          <dt>Topshiriq</dt>
          <dd>{stats.task_count} · {stats.line_count} satr · {qty(stats.actual_qty)} dona</dd>
          <dt>Ish vaqti</dt>
          <dd>{stats.work_seconds > 0 ? duration(stats.work_seconds) : "—"} (kutish {stats.wait_seconds > 0 ? duration(stats.wait_seconds) : "—"})</dd>
          <dt>Unumdorlik</dt>
          <dd>{perHourText(stats.line_count, stats.work_seconds)} satr/soat</dd>
          <dt>Tafovut</dt>
          <dd>kamomad {stats.shortage_count} · ortiqcha {stats.excess_count} · jarima {stats.penalty_count}</dd>
          <dt>Faol kun</dt>
          <dd>{stats.active_days}</dd>
          <dt>Qo'lida</dt>
          <dd>{stats.open_tasks} ochiq topshiriq</dd>
        </dl>

        <h3 className="section-title">Topshiriq turi bo'yicha</h3>
        {stats.by_type.length === 0 ? (
          <p className="staff-muted">Davrda yakunlangan topshiriq yo'q.</p>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Tur</th>
                <th style={{ textAlign: "right" }}>Topshiriq</th>
                <th style={{ textAlign: "right" }}>Satr</th>
                <th style={{ textAlign: "right" }}>Dona</th>
                <th style={{ textAlign: "right" }}>Satr/soat</th>
              </tr>
            </thead>
            <tbody>
              {stats.by_type.map((item) => (
                <tr key={item.task_type}>
                  <td>{statusLabel(item.task_type ?? "")}</td>
                  <td className="num" style={{ textAlign: "right" }}>{item.task_count}</td>
                  <td className="num" style={{ textAlign: "right" }}>{item.line_count}</td>
                  <td className="num" style={{ textAlign: "right" }}>{qty(item.actual_qty)}</td>
                  <td className="num" style={{ textAlign: "right" }}>{perHourText(item.line_count, item.work_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3 className="section-title">Kunlar bo'yicha</h3>
        {daily.isLoading ? (
          <div className="card skeleton" style={{ height: 80 }} />
        ) : days.length === 0 ? (
          <p className="staff-muted">Davrda faoliyat yo'q.</p>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Kun</th>
                <th style={{ textAlign: "right" }}>Topshiriq</th>
                <th style={{ textAlign: "right" }}>Satr</th>
                <th style={{ textAlign: "right" }}>Dona</th>
                <th style={{ textAlign: "right" }}>Ish vaqti</th>
                <th style={{ textAlign: "right" }}>Tafovut</th>
              </tr>
            </thead>
            <tbody>
              {days.map(([day, entry]) => (
                <tr key={day}>
                  <td className="mono">{day}</td>
                  <td className="num" style={{ textAlign: "right" }}>{entry.tasks}</td>
                  <td className="num" style={{ textAlign: "right" }}>{entry.lines}</td>
                  <td className="num" style={{ textAlign: "right" }}>{qty(String(entry.qty))}</td>
                  <td className="num" style={{ textAlign: "right" }}>{entry.seconds > 0 ? duration(entry.seconds) : "—"}</td>
                  <td className="num" style={{ textAlign: "right" }}>{entry.issues}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3 className="section-title">Oxirgi topshiriqlar</h3>
        {tasks.isLoading ? (
          <div className="card skeleton" style={{ height: 80 }} />
        ) : (tasks.data?.items ?? []).length === 0 ? (
          <p className="staff-muted">Topshiriq yo'q.</p>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Tur</th>
                <th>Holat</th>
                <th style={{ textAlign: "right" }}>Satr</th>
                <th style={{ textAlign: "right" }}>Fakt</th>
                <th>Vaqt</th>
              </tr>
            </thead>
            <tbody>
              {(tasks.data?.items ?? []).map((task) => (
                <tr key={task.task_id}>
                  <td>{statusLabel(task.task_type ?? "")}</td>
                  <td><StatusBadge value={task.status ?? ""} /></td>
                  <td className="num" style={{ textAlign: "right" }}>{task.line_count}</td>
                  <td className="num" style={{ textAlign: "right" }}>{qty(task.actual_qty)}</td>
                  <td title={dateTime(task.completed_at || task.created_at)}>{relative(task.completed_at || task.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Drawer>
  );
}

/** Excel uchun CSV: `;` ajratuvchi va BOM — rus/o'zbek Excel shunday ochadi. */
function exportCsv(rows: Row[], range: { from: string; to: string }) {
  const cell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const header = ["Xodim", "Rol", "Faol", "Topshiriq", "Satr", "Dona", "Ish vaqti, soat", "Kutish, soat",
    "Satr/soat", "Kamomad", "Ortiqcha", "Jarima", "Faol kun", "Qo'lida", "Oxirgi ish"];
  const hours = (seconds: number) => (seconds / 3600).toFixed(2).replace(".", ",");
  const lines = [header.map(cell).join(";")];
  for (const row of rows) {
    const stats = row.stats;
    lines.push([
      cell(row.name), cell(row.roles.map(roleLabel).join(", ")), cell(row.isActive ? "ha" : "yo'q"),
      cell(stats.task_count), cell(stats.line_count), cell(stats.actual_qty.replace(".", ",")),
      cell(hours(stats.work_seconds)), cell(hours(stats.wait_seconds)),
      cell(perHourText(stats.line_count, stats.work_seconds).replace(".", ",")),
      cell(stats.shortage_count), cell(stats.excess_count), cell(stats.penalty_count),
      cell(stats.active_days), cell(stats.open_tasks), cell(dateTime(stats.last_completed_at)),
    ].join(";"));
  }
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `kpi-xodimlar-${range.from}_${range.to}.csv`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
